'use client';

import { c as _c } from "react/compiler-runtime";
import { Globe } from '@byway/icons';
import { useUser } from '@clerk/nextjs';
import { useLocale, useTranslations } from 'next-intl';
import { useContext, useState } from 'react';
import { DialogTrigger, Heading, OverlayTriggerStateContext, Radio, RadioGroup, Tabs } from 'react-aria-components';
import { isInternalUser } from 'utils';
import { Button } from '~/components/Button';
import { Dialog, DialogHeader, DialogScrollableContent } from '~/components/Dialog';
import { Label } from '~/components/Form/Label';
import { Tab, TabContent, TabsList } from '~/components/Tabs';
import { COUNTRY_COOKIE, LOCALE_COOKIE } from '~/constants/cookies';
import { useCurrencyContext } from '~/contexts/Currencies';
import { CONTENT_LOCALES_META } from '~/i18n/buildAlternates';
import { PREFERENCE_COUNTRIES } from '~/i18n/currency-map';
import { i18n, type Locale } from '~/i18n/i18n-config';
import { getPathname, usePathname } from '~/i18n/navigation';
import { logError } from '~/utils';
import { getCookie, setSiteCookie } from '~/utils/cookies';
import { CURRENCIES, type CurrencyName, getCurrencySymbol } from '~/utils/currencyHelpers';
import styles from './PreferencesDialog.module.css';

// CLDR has no short name for some regions ("Nederland"): use the code
const localeLabel = (locale: Locale) => {
  const [language, region] = locale.split('-');
  const shortRegion = new Intl.DisplayNames([locale], {
    type: 'region',
    style: 'short'
  }).of(region) ?? region;
  const languageName = new Intl.DisplayNames([locale], {
    type: 'language'
  }).of(language);
  return `${languageName} (${shortRegion.length <= 3 ? shortRegion : region})`;
};

// No meta: not a CMS record page, exists in every locale
const isPageMissingIn = (locale: Locale) => {
  const contentLocales = globalThis.document.querySelector(`meta[name="${CONTENT_LOCALES_META}"]`)?.getAttribute('content')?.split(',');
  return contentLocales !== undefined && !contentLocales.includes(locale);
};
const resolveLocaleHref = (locale: Locale, pathname: string) => isPageMissingIn(locale) ? getPathname({
  href: '/',
  locale
}) : getPathname({
  href: pathname,
  locale
}) + globalThis.location.search;
const getCountry = () => getCookie(COUNTRY_COOKIE) ?? 'GB';

// Drafts until Confirm: the modal unmounts on close, so any other close discards them
export const PreferencesContent = () => {
  const $ = _c(110);
  const t = useTranslations();
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const {
    user
  } = useUser();
  const {
    displayCurrency,
    setDisplayCurrency
  } = useCurrencyContext();
  const overlayState = useContext(OverlayTriggerStateContext);
  const [selectedCountry, setSelectedCountry] = useState(getCountry);
  const [selectedCurrency, setSelectedCurrency] = useState(displayCurrency);
  const [selectedLocale, setSelectedLocale] = useState(currentLocale);
  const [isSaving, setIsSaving] = useState(false);
  const primaryEmail = user?.primaryEmailAddress;
  const isStaff = isInternalUser({
    email: primaryEmail?.emailAddress,
    email_verified: primaryEmail?.verification?.status === "verified"
  });
  const locales = isStaff ? i18n.locales : i18n.liveLocales;
  const showCountry = process.env.NEXT_PUBLIC_FF_ENABLE_COUNTRY_SELECTOR === "true";
  const showLanguage = locales.length > 1;
  let T0;
  let T1;
  let T2;
  let T3;
  let confirm;
  let t0;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== currentLocale || $[1] !== displayCurrency || $[2] !== overlayState || $[3] !== pathname || $[4] !== selectedCountry || $[5] !== selectedCurrency || $[6] !== selectedLocale || $[7] !== setDisplayCurrency || $[8] !== showLanguage || $[9] !== t || $[10] !== user) {
    const regionNames = new Intl.DisplayNames([currentLocale], {
      type: "region"
    });
    const currencyNames = new Intl.DisplayNames([currentLocale], {
      type: "currency"
    });
    let t12;
    if ($[28] !== currentLocale || $[29] !== displayCurrency || $[30] !== overlayState || $[31] !== pathname || $[32] !== selectedCountry || $[33] !== selectedCurrency || $[34] !== selectedLocale || $[35] !== setDisplayCurrency || $[36] !== user) {
      t12 = async () => {
        if (selectedCountry !== getCountry()) {
          setSiteCookie(COUNTRY_COOKIE, selectedCountry);
        }
        if (selectedCurrency !== displayCurrency) {
          setDisplayCurrency?.(selectedCurrency);
        }
        if (selectedLocale === currentLocale) {
          overlayState?.close();
          return;
        }
        setIsSaving(true);
        setSiteCookie(LOCALE_COOKIE, selectedLocale);
        if (user) {
          await user.update({
            unsafeMetadata: {
              ...user.unsafeMetadata,
              locale: selectedLocale
            }
          }).catch(logError);
        }
        globalThis.location.assign(resolveLocaleHref(selectedLocale, pathname));
      };
      $[28] = currentLocale;
      $[29] = displayCurrency;
      $[30] = overlayState;
      $[31] = pathname;
      $[32] = selectedCountry;
      $[33] = selectedCurrency;
      $[34] = selectedLocale;
      $[35] = setDisplayCurrency;
      $[36] = user;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    confirm = t12;
    T3 = Tabs;
    t9 = styles.tabs;
    let t13;
    if ($[38] !== t) {
      t13 = t("aria.preferences_change");
      $[38] = t;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    if ($[40] !== t13) {
      t10 = <Heading slot="title" className="sr-only">{t13}</Heading>;
      $[40] = t13;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    let t14;
    if ($[42] !== t) {
      t14 = t("aria.preferences_change");
      $[42] = t;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    let t15;
    if ($[44] !== t) {
      t15 = showCountry && <Tab variant="minimal" id="country">{t("navigation.country")}</Tab>;
      $[44] = t;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t16;
    if ($[46] !== t) {
      t16 = t("navigation.currency");
      $[46] = t;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    let t17;
    if ($[48] !== t16) {
      t17 = <Tab variant="minimal" id="currency">{t16}</Tab>;
      $[48] = t16;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    let t18;
    if ($[50] !== showLanguage || $[51] !== t) {
      t18 = showLanguage && <Tab variant="minimal" id="language">{t("navigation.language")}</Tab>;
      $[50] = showLanguage;
      $[51] = t;
      $[52] = t18;
    } else {
      t18 = $[52];
    }
    if ($[53] !== t14 || $[54] !== t15 || $[55] !== t17 || $[56] !== t18) {
      t11 = <DialogHeader><TabsList variant="minimal" ariaLabel={t14}>{t15}{t17}{t18}</TabsList></DialogHeader>;
      $[53] = t14;
      $[54] = t15;
      $[55] = t17;
      $[56] = t18;
      $[57] = t11;
    } else {
      t11 = $[57];
    }
    T2 = DialogScrollableContent;
    t8 = showCountry && <TabContent id="country"><RadioGroup className={styles.group} value={selectedCountry} onChange={setSelectedCountry} data-test-id="preferences-country"><Label>{t("navigation.country_select")}</Label><div className={styles.options}>{PREFERENCE_COUNTRIES.map(code => <Radio key={code} value={code} className={styles.option}>{regionNames.of(code)}</Radio>)}</div></RadioGroup></TabContent>;
    T1 = TabContent;
    t7 = "currency";
    T0 = RadioGroup;
    t2 = styles.group;
    t3 = selectedCurrency;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = value => setSelectedCurrency(value as CurrencyName);
      $[58] = t4;
    } else {
      t4 = $[58];
    }
    t5 = "preferences-currency";
    let t19;
    if ($[59] !== t) {
      t19 = t("navigation.currency_select");
      $[59] = t;
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    if ($[61] !== t19) {
      t6 = <Label>{t19}</Label>;
      $[61] = t19;
      $[62] = t6;
    } else {
      t6 = $[62];
    }
    t0 = styles.options;
    t1 = CURRENCIES.map(code_0 => {
      const symbol = getCurrencySymbol(code_0);
      return <Radio key={code_0} value={code_0} className={styles.option}>{currencyNames.of(code_0)}<span className={styles.description}>{symbol === code_0 ? code_0 : `${code_0} – ${symbol}`}</span></Radio>;
    });
    $[0] = currentLocale;
    $[1] = displayCurrency;
    $[2] = overlayState;
    $[3] = pathname;
    $[4] = selectedCountry;
    $[5] = selectedCurrency;
    $[6] = selectedLocale;
    $[7] = setDisplayCurrency;
    $[8] = showLanguage;
    $[9] = t;
    $[10] = user;
    $[11] = T0;
    $[12] = T1;
    $[13] = T2;
    $[14] = T3;
    $[15] = confirm;
    $[16] = t0;
    $[17] = t1;
    $[18] = t10;
    $[19] = t11;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    T0 = $[11];
    T1 = $[12];
    T2 = $[13];
    T3 = $[14];
    confirm = $[15];
    t0 = $[16];
    t1 = $[17];
    t10 = $[18];
    t11 = $[19];
    t2 = $[20];
    t3 = $[21];
    t4 = $[22];
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t12;
  if ($[63] !== t0 || $[64] !== t1) {
    t12 = <div className={t0}>{t1}</div>;
    $[63] = t0;
    $[64] = t1;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  let t13;
  if ($[66] !== T0 || $[67] !== t12 || $[68] !== t2 || $[69] !== t3 || $[70] !== t4 || $[71] !== t5 || $[72] !== t6) {
    t13 = <T0 className={t2} value={t3} onChange={t4} data-test-id={t5}>{t6}{t12}</T0>;
    $[66] = T0;
    $[67] = t12;
    $[68] = t2;
    $[69] = t3;
    $[70] = t4;
    $[71] = t5;
    $[72] = t6;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== T1 || $[75] !== t13 || $[76] !== t7) {
    t14 = <T1 id={t7}>{t13}</T1>;
    $[74] = T1;
    $[75] = t13;
    $[76] = t7;
    $[77] = t14;
  } else {
    t14 = $[77];
  }
  let t15;
  if ($[78] !== currentLocale || $[79] !== locales || $[80] !== selectedLocale || $[81] !== showLanguage || $[82] !== t) {
    t15 = showLanguage && <TabContent id="language"><RadioGroup className={styles.group} value={selectedLocale} onChange={value_0 => setSelectedLocale(value_0 as Locale)} data-test-id="preferences-language"><Label>{t("navigation.language_select")}</Label><div className={styles.options}>{locales.map(locale => <Radio key={locale} value={locale} className={styles.option}>{localeLabel(locale)}{locale !== currentLocale && isPageMissingIn(locale) && <span className={styles.description}>{t("navigation.language_homepage")}</span>}</Radio>)}</div></RadioGroup></TabContent>;
    $[78] = currentLocale;
    $[79] = locales;
    $[80] = selectedLocale;
    $[81] = showLanguage;
    $[82] = t;
    $[83] = t15;
  } else {
    t15 = $[83];
  }
  let t16;
  if ($[84] !== T2 || $[85] !== t14 || $[86] !== t15 || $[87] !== t8) {
    t16 = <T2>{t8}{t14}{t15}</T2>;
    $[84] = T2;
    $[85] = t14;
    $[86] = t15;
    $[87] = t8;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  let t17;
  if ($[89] !== t) {
    t17 = t("forms.cancel");
    $[89] = t;
    $[90] = t17;
  } else {
    t17 = $[90];
  }
  let t18;
  if ($[91] !== isSaving || $[92] !== t17) {
    t18 = <Button slot="close" variant="secondary" isDisabled={isSaving} dataTestId="preferences-cancel" size="extraSmall">{t17}</Button>;
    $[91] = isSaving;
    $[92] = t17;
    $[93] = t18;
  } else {
    t18 = $[93];
  }
  let t19;
  if ($[94] !== t) {
    t19 = t("forms.confirm");
    $[94] = t;
    $[95] = t19;
  } else {
    t19 = $[95];
  }
  let t20;
  if ($[96] !== confirm || $[97] !== isSaving || $[98] !== t19) {
    t20 = <Button variant="primary" onPress={confirm} isLoading={isSaving} isDisabled={isSaving} dataTestId="preferences-confirm" size="extraSmall">{t19}</Button>;
    $[96] = confirm;
    $[97] = isSaving;
    $[98] = t19;
    $[99] = t20;
  } else {
    t20 = $[99];
  }
  let t21;
  if ($[100] !== t18 || $[101] !== t20) {
    t21 = <div className={styles.footer}>{t18}{t20}</div>;
    $[100] = t18;
    $[101] = t20;
    $[102] = t21;
  } else {
    t21 = $[102];
  }
  let t22;
  if ($[103] !== T3 || $[104] !== t10 || $[105] !== t11 || $[106] !== t16 || $[107] !== t21 || $[108] !== t9) {
    t22 = <T3 className={t9}>{t10}{t11}{t16}{t21}</T3>;
    $[103] = T3;
    $[104] = t10;
    $[105] = t11;
    $[106] = t16;
    $[107] = t21;
    $[108] = t9;
    $[109] = t22;
  } else {
    t22 = $[109];
  }
  return t22;
};
export const PreferencesDialog = () => {
  const $ = _c(8);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("aria.preferences_change");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Globe decorative={true} size="large" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = <Button ariaLabel={t0} variant="icon" dataTestId="preferences-trigger" data-ua-click="edit-currency-popover">{t1}</Button>;
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Dialog className={styles.dialog}><PreferencesContent /></Dialog>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <DialogTrigger>{t2}{t3}</DialogTrigger>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};